import { useState, useEffect, useMemo } from 'react'
import { ArrowLeft, X, Plus } from 'lucide-react'
import { useMatch, useUpdateMatch } from '../hooks/useMatches'
import Spinner from './Spinner'
import logger from '../utils/logger'

const ORDINALS = ['1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th']

export default function EventBuyInPage({ matchId, isEditing, onBack, onContinue }) {
  const { data: match, isLoading } = useMatch(matchId)
  const updateMatch = useUpdateMatch()
  const [buyIn, setBuyIn] = useState('')
  const [buyInInitialized, setBuyInInitialized] = useState(false)
  const [payoutPreset, setPayoutPreset] = useState('winner_take_all')
  const [customAmounts, setCustomAmounts] = useState(['', ''])
  const [payoutInitialized, setPayoutInitialized] = useState(false)

  const matchPlayers = match?.match_players || []
  const matchTeams = match?.match_teams || []
  const numTeams = matchTeams.length
  const buyInNum = parseFloat(buyIn) || 0
  const totalPot = matchPlayers.length * buyInNum
  const isInvitational = match?.match_type === 'invitational'
  const hasBuyIn = buyIn && Number(buyIn) > 0
  const playersPerTeam = numTeams > 0 ? matchPlayers.length / numTeams : 1

  // Initialize buy-in from match data
  useEffect(() => {
    if (match && !buyInInitialized) {
      setBuyIn(match.buy_in ? String(match.buy_in) : '')
      setBuyInInitialized(true)
    }
  }, [match, buyInInitialized])

  // Initialize payout config from match data
  useEffect(() => {
    if (match && buyInInitialized && !payoutInitialized) {
      const cfg = match.payout_config
      if (cfg?.preset) {
        setPayoutPreset(cfg.preset)
        if (cfg.preset === 'custom' && cfg.splits) {
          const pot = matchPlayers.length * (match.buy_in || 0)
          setCustomAmounts(cfg.splits.map(pct => pot > 0 ? String(Math.round(pct / 100 * pot)) : ''))
        }
      }
      setPayoutInitialized(true)
    }
  }, [match, buyInInitialized, payoutInitialized, matchPlayers.length])

  // Compute current splits as percentages
  const currentSplits = useMemo(() => {
    if (payoutPreset === 'winner_take_all') return [100]
    if (payoutPreset === '80_20') return [80, 20]
    // Custom: convert dollar amounts to percentages
    if (totalPot <= 0) return customAmounts.map(() => 0)
    return customAmounts.map(a => {
      const val = parseFloat(a) || 0
      return Math.round((val / totalPot) * 100)
    })
  }, [payoutPreset, customAmounts, totalPot])

  const splitsTotal = currentSplits.reduce((s, v) => s + v, 0)
  const customDollarTotal = payoutPreset === 'custom'
    ? customAmounts.reduce((s, a) => s + (parseFloat(a) || 0), 0)
    : 0
  const potRemaining = totalPot - customDollarTotal
  const splitsValid = payoutPreset !== 'custom' || (splitsTotal === 100 && customAmounts.every(a => parseFloat(a) > 0))
  const canContinue = hasBuyIn && splitsValid

  const handlePresetChange = (preset) => {
    setPayoutPreset(preset)
    if (preset === 'custom') {
      // Initialize custom amounts from current pot — default 60/40 for 2 places
      if (totalPot > 0) {
        setCustomAmounts([String(Math.round(totalPot * 0.6)), String(Math.round(totalPot * 0.4))])
      } else {
        setCustomAmounts(['', ''])
      }
    }
  }

  const handleCustomAmountChange = (index, value) => {
    const next = [...customAmounts]
    next[index] = value
    setCustomAmounts(next)
  }

  const addPlace = () => {
    if (customAmounts.length < numTeams) {
      setCustomAmounts([...customAmounts, ''])
    }
  }

  const removePlace = (index) => {
    if (customAmounts.length > 1) {
      setCustomAmounts(customAmounts.filter((_, i) => i !== index))
    }
  }

  const handleContinue = () => {
    const val = parseFloat(buyIn) || 0
    const updates = {}

    if (val !== (match.buy_in || 0)) {
      updates.buy_in = val
    }

    // Always save payout config when there's a buy-in
    updates.payout_type = 'placement'
    updates.payout_config = { preset: payoutPreset, splits: currentSplits }

    if ((isInvitational || match.match_type === 'team_match') && match.status !== 'active' && match.status !== 'completed') {
      updates.status = 'active'
    }

    if (Object.keys(updates).length > 0) {
      updateMatch.mutate({ id: matchId, ...updates }, {
        onSuccess: () => onContinue(),
        onError: (err) => {
          logger.capture(err, { action: 'saveBuyIn', matchId, matchType: match.match_type, updates })
          alert('Failed to save buy-in. Please try again.')
        },
      })
    } else {
      onContinue()
    }
  }

  if (isLoading || !match) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <Spinner message="Loading..." />
      </div>
    )
  }

  // Available presets based on team count
  const presets = [
    { key: 'winner_take_all', label: 'Winner Take All' },
    ...(numTeams >= 3 ? [{ key: '80_20', label: '80 / 20' }] : []),
    { key: 'custom', label: 'Custom' },
  ]

  // Validation error message
  const getErrorMessage = () => {
    if (!hasBuyIn) return 'Set buy-in to continue'
    if (payoutPreset === 'custom' && splitsTotal !== 100) {
      if (potRemaining > 0) return `$${potRemaining} unallocated — payouts must total $${totalPot}`
      if (potRemaining < 0) return `$${Math.abs(potRemaining)} over-allocated — payouts must total $${totalPot}`
    }
    if (payoutPreset === 'custom' && customAmounts.some(a => !a || parseFloat(a) <= 0)) {
      return 'All payout amounts must be greater than $0'
    }
    return null
  }

  const errorMessage = getErrorMessage()

  return (
    <div>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        marginBottom: '1.5rem',
      }}>
        <button
          onClick={onBack}
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '9999px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'var(--bg-tertiary)',
            flexShrink: 0,
          }}
        >
          <ArrowLeft size={20} />
        </button>
        <h1 style={{ fontSize: '1.25rem', fontWeight: '700' }}>Buy-In & Payout</h1>
      </div>

      {/* Content */}
      <div style={{ paddingBottom: '6rem' }}>
        {/* Buy-In Section Header */}
        <h2 style={{ fontSize: '1.125rem', fontWeight: '700', color: 'var(--accent-primary)', marginBottom: '0.75rem' }}>
          Buy-In
        </h2>
        <div style={{
          padding: '0.75rem',
          backgroundColor: 'var(--bg-secondary)',
          borderRadius: '0.5rem',
          border: '1px solid var(--border-color)',
        }}>
          <label style={{
            display: 'block',
            fontSize: '0.75rem',
            fontWeight: '700',
            color: 'var(--text-muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: '0.5rem',
          }}>
            Buy-In (per player)
          </label>
          <div style={{ position: 'relative' }}>
            <span style={{
              position: 'absolute',
              left: '0.75rem',
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--text-muted)',
              fontSize: '0.875rem',
              fontWeight: '600',
              pointerEvents: 'none',
            }}>$</span>
            <input
              type="number"
              inputMode="numeric"
              pattern="[0-9]*"
              min="0"
              step="1"
              value={buyIn}
              onChange={(e) => setBuyIn(e.target.value)}
              onBlur={() => {
                const val = parseFloat(buyIn) || 0
                if (val !== (match.buy_in || 0)) {
                  updateMatch.mutate({ id: matchId, buy_in: val })
                }
              }}
              placeholder="e.g. 50"
              style={{
                width: '100%',
                paddingLeft: '1.75rem',
                height: '40px',
                borderRadius: '0.5rem',
                border: '1px solid var(--border-color)',
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                fontSize: '0.875rem',
              }}
            />
          </div>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.6875rem', marginTop: '0.375rem' }}>
            {hasBuyIn
              ? `${matchPlayers.length} players × $${Number(buyIn)} = $${totalPot} total pot`
              : 'Required — set the entry fee for this match'}
          </p>
        </div>

        {/* Payout Section */}
          <h2 style={{ fontSize: '1.125rem', fontWeight: '700', color: 'var(--accent-primary)', marginTop: '1.5rem', marginBottom: '0.75rem' }}>
            Payout
          </h2>
          <div style={{
            padding: '0.75rem',
            backgroundColor: 'var(--bg-secondary)',
            borderRadius: '0.5rem',
            border: '1px solid var(--border-color)',
          }}>

            {/* Preset Buttons */}
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
              {presets.map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => handlePresetChange(key)}
                  style={{
                    flex: 1,
                    padding: '0.5rem 0.25rem',
                    borderRadius: '9999px',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    border: payoutPreset === key
                      ? '2px solid var(--accent-primary)'
                      : '1px solid var(--border-color)',
                    backgroundColor: payoutPreset === key
                      ? 'var(--accent-primary-alpha, rgba(14, 164, 122, 0.1))'
                      : 'var(--bg-tertiary)',
                    color: payoutPreset === key
                      ? 'var(--accent-primary)'
                      : 'var(--text-secondary)',
                  }}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Preset Summary (non-custom) */}
            {payoutPreset !== 'custom' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                {currentSplits.map((pct, i) => {
                  const dollarAmount = Math.round(pct / 100 * totalPot)
                  const perPlayer = Math.round(dollarAmount / playersPerTeam - buyInNum)
                  return (
                    <div key={i} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '0.5rem 0.625rem',
                      backgroundColor: 'var(--bg-tertiary)',
                      borderRadius: '0.375rem',
                    }}>
                      <span style={{ fontSize: '0.8125rem', fontWeight: '600', color: 'var(--text-primary)' }}>
                        {ORDINALS[i]} Place
                      </span>
                      <div style={{ textAlign: 'right' }}>
                        <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                          ${dollarAmount} ({pct}%)
                        </span>
                        <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', display: 'block' }}>
                          {perPlayer >= 0 ? '+' : ''}${perPlayer}/player
                        </span>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}

            {/* Custom Editor */}
            {payoutPreset === 'custom' && (
              <div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {customAmounts.map((amount, i) => {
                    const dollarVal = parseFloat(amount) || 0
                    const pct = totalPot > 0 ? Math.round(dollarVal / totalPot * 100) : 0
                    const perPlayer = Math.round(dollarVal / playersPerTeam - buyInNum)
                    return (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{
                          fontSize: '0.8125rem',
                          fontWeight: '600',
                          color: 'var(--text-primary)',
                          width: '3rem',
                          flexShrink: 0,
                        }}>
                          {ORDINALS[i]}
                        </span>
                        <div style={{ position: 'relative', flex: 1 }}>
                          <span style={{
                            position: 'absolute',
                            left: '0.625rem',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: 'var(--text-muted)',
                            fontSize: '0.8125rem',
                            fontWeight: '600',
                            pointerEvents: 'none',
                          }}>$</span>
                          <input
                            type="number"
                            inputMode="numeric"
                            min="0"
                            value={amount}
                            onChange={(e) => handleCustomAmountChange(i, e.target.value)}
                            placeholder="0"
                            style={{
                              width: '100%',
                              paddingLeft: '1.5rem',
                              height: '36px',
                              borderRadius: '0.375rem',
                              border: '1px solid var(--border-color)',
                              backgroundColor: 'var(--bg-secondary)',
                              color: 'var(--text-primary)',
                              fontSize: '0.8125rem',
                            }}
                          />
                        </div>
                        <div style={{ textAlign: 'right', flexShrink: 0, width: '3.5rem' }}>
                          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'block' }}>
                            {pct}%
                          </span>
                          {dollarVal > 0 && (
                            <span style={{ fontSize: '0.625rem', color: 'var(--text-muted)', display: 'block' }}>
                              {perPlayer >= 0 ? '+' : ''}${perPlayer}/pp
                            </span>
                          )}
                        </div>
                        {i > 0 && (
                          <button
                            onClick={() => removePlace(i)}
                            style={{
                              width: '28px',
                              height: '28px',
                              borderRadius: '9999px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: 'var(--bg-tertiary)',
                              border: '1px solid var(--border-color)',
                              flexShrink: 0,
                            }}
                          >
                            <X size={14} color="var(--text-muted)" />
                          </button>
                        )}
                        {i === 0 && <div style={{ width: '28px', flexShrink: 0 }} />}
                      </div>
                    )
                  })}
                </div>

                {/* Add Place button */}
                {customAmounts.length < numTeams && (
                  <button
                    onClick={addPlace}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.375rem',
                      marginTop: '0.5rem',
                      padding: '0.375rem 0.625rem',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      color: 'var(--accent-primary)',
                      backgroundColor: 'transparent',
                      border: 'none',
                    }}
                  >
                    <Plus size={14} /> Add {ORDINALS[customAmounts.length]} Place
                  </button>
                )}

                {/* Pot Remaining indicator */}
                <div style={{
                  marginTop: '0.625rem',
                  padding: '0.5rem 0.625rem',
                  borderRadius: '0.375rem',
                  backgroundColor: 'var(--bg-tertiary)',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  color: potRemaining === 0
                    ? '#22c55e'
                    : potRemaining < 0
                      ? '#ef4444'
                      : '#f59e0b',
                }}>
                  {potRemaining === 0
                    ? 'Fully allocated'
                    : potRemaining > 0
                      ? `Pot Remaining: $${potRemaining} (${Math.round(potRemaining / totalPot * 100)}%)`
                      : `Over-allocated by $${Math.abs(potRemaining)}`}
                </div>
              </div>
            )}
          </div>
      </div>

      {/* Fixed bottom bar */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        padding: '0.75rem 1rem',
        paddingBottom: 'max(1rem, calc(0.25rem + var(--bottom-safe-area, 0px)))',
        backgroundColor: 'var(--bg-primary)',
        zIndex: 101,
      }}>
        {errorMessage && (
          <p style={{
            textAlign: 'center',
            fontSize: '0.75rem',
            color: '#ef4444',
            marginBottom: '0.375rem',
          }}>
            {errorMessage}
          </p>
        )}
        <button
          onClick={handleContinue}
          disabled={!canContinue}
          className="btn-primary"
          style={{
            width: '100%',
            height: '48px',
            fontSize: '1rem',
            fontWeight: '600',
            borderRadius: '9999px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: !canContinue ? 0.5 : 1,
          }}
        >
          {isEditing
            ? 'Save'
            : match.match_type === 'team_match' ? 'Continue to Session Setup'
            : match.match_type === 'invitational' ? 'Activate Invitational' : 'Activate Golf Trip'}
        </button>
      </div>
    </div>
  )
}
